import React, { useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { useTheme } from 'styled-components';
import * as yup from 'yup';
import { Link } from 'gatsby';

import { CartContext } from '@repo/contexts/CartContext';
import { LocaleContext } from '@repo/contexts/LocaleContext';
import { DealerSearchContext } from '@repo/contexts/DealerSearchContext';

import {
  CREATE_CART_QUERY,
  FETCH_PRODUCT_UPDATE_QUERY,
} from '@repo/contexts/cartQueries';

import breakpoints from '@repo/ui-components/breakpoints';
import sharedStyles from '@repo/ui-components/styles';
import Spinner from '@repo/ui-components/form/Spinner';
import Button from '@repo/ui-components/button/Button';
import MessageBox from '@repo/ui-components/form/MessageBox';

// eslint-disable-next-line import/no-cycle
import BlockBuilder from '@repo/ui-components/sanity/BlockBuilder';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

const SubscriptionsHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.brandPrimary};
  padding: ${sharedStyles.spacing['spacing-md']};
  margin-bottom: ${sharedStyles.spacing['spacing-md']};

  h2,
  p {
    color: ${({ theme }) => theme.colors.brandSecondary};
  }
`;

const SubscriptionsContainer = styled.div`
  display: grid;
  gap: ${sharedStyles.spacing['spacing-lg']};
  grid-template-columns: 1fr;

  @media (min-width: ${breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const PlanContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.cardBackground};
  color: ${({ theme }) => theme.colors.cardText};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${sharedStyles.spacing['spacing-md']};
  grid-gap: ${sharedStyles.spacing['spacing-sm']};
  display: grid;
  grid-template-columns: 1fr;
  grid-column: span 2;

  p {
    margin: 0;
  }

  @media (min-width: ${breakpoints.md}) {
    text-align: center;
  }

  @media (min-width: ${breakpoints.lg}) {
    text-align: left;
    grid-column: span 1;
  }
`;

const TermsContainer = styled.div`
  display: block;
`;

const ButtonContainer = styled.div`
  text-align: center;
`;

const OldPrice = styled.p`
  text-decoration: line-through;
  font-weight: ${sharedStyles.fontWeight.semiBold};
  font-size: ${sharedStyles.fontSize.lg};
  margin: 0;
  text-align: center;
`;

const Price = styled.p`
  color: ${({ theme }) => theme.colors.headline};
  font-weight: ${sharedStyles.fontWeight.semiBold};
  font-size: ${sharedStyles.fontSize['2xl']};
  margin: 0;
  text-align: center;
`;

const FreeTrial = styled.p`
  margin-block: ${sharedStyles.spacing['spacing-md']};
`;

const SpinnerContainer = styled.div`
  padding-top: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

const SUBSCRIPTION_CART_ID = '_subscriptionCartId';
const SUBSCRIPTION_FREQUENCY_METAFIELD = 'subscription_frequency';

function BlockBuilderSubscriptionGrid({ value, getInternalLink }) {
  const theme = useTheme();
  const { tt } = useUiComponentsInternationalContext();

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        email: yup
          .string()
          .trim()
          .email(
            tt(
              s =>
                s.blockBuilderSubscriptionGridComponent
                  .useValidEmailAddressValidationMessage
            )
          ),
        iotUserId: yup.string(),
        iotGroupId: yup.string(),
        deviceId: yup.string(),
        collarId: yup.string(),
        thingName: yup.string(),
        petName: yup.string(),
        petId: yup.string(),
        token: yup.string(),
        customerAccessToken: yup.string(),
        customerAccessTokenExpiresAt: yup.string(),
      }),
    []
  );

  const { shopifyClient } = useContext(CartContext);
  const localeContext = useContext(LocaleContext);
  const dealerContext = useContext(DealerSearchContext);

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const countryCode = localeContext?.getCountryCode() || 'US';

  const genericErrorMessage = (
    <>
      <p>
        {tt(
          s =>
            s.blockBuilderSubscriptionGridComponent
              .errorOccuredDuringSubscriptionMessage,
          {
            link: (
              <Link to="/app/dashboard">
                {tt(
                  s =>
                    s.blockBuilderSubscriptionGridComponent
                      .returnToInvisibleFenceAppLink
                )}
              </Link>
            ),
          }
        )}
      </p>
      <p>
        {tt(
          s =>
            s.blockBuilderSubscriptionGridComponent
              .weApologizeForInconvenienceMessage
        )}
      </p>
    </>
  );

  // User Input from Token
  const [user, setUser] = useState({
    email: '',
    iotUserId: '',
    iotGroupId: '',
    deviceId: '',
    collarId: '',
    thingName: '',
    petName: '',
    petId: '',
    multipassToken: '',
    customerAccessToken: '',
    customerAccessTokenExpiresAt: '',
  });

  const createCart = async (
    attributes = [],
    buyerIdentity = {},
    discountCodes = [],
    lines = [],
    metafields = [],
    note = ''
  ) => {
    // Create a new cart for subscription and store id in localStorage
    const newCart = await shopifyClient.request(CREATE_CART_QUERY, {
      input: {
        attributes: [
          ...attributes,
          ...(process.env.GATSBY_RSC_ENV
            ? [
                {
                  key: 'rscEnv',
                  value: process.env.GATSBY_RSC_ENV,
                },
              ]
            : []),
        ],
        buyerIdentity: {
          countryCode: localeContext?.getCountryCode() || 'US',
          ...buyerIdentity,
        },
        discountCodes,
        lines,
        metafields,
        note,
      },
    });

    if (newCart?.cartCreate?.userErrors?.length > 0) {
      setErrorMessage(genericErrorMessage);
    }

    if (newCart?.cartCreate?.cart) {
      localStorage.setItem(SUBSCRIPTION_CART_ID, newCart?.cartCreate?.cart?.id);
      return newCart?.cartCreate?.cart;
    }

    return null;
  };

  const createCheckoutUrl = async (
    attributes = [],
    buyerIdentity = {},
    discountCodes = [],
    lines = [],
    metafields = [],
    note = ''
  ) => {
    // Create a new cart for subscription and store id in localStorage
    const cart = await createCart(
      attributes,
      buyerIdentity,
      discountCodes,
      lines,
      metafields,
      note
    );

    if (cart) {
      localStorage.setItem(SUBSCRIPTION_CART_ID, cart?.id);
      return cart?.checkoutUrl;
    }

    return '';
  };

  // IFB Only:
  // IFB is using old Recharge and have different variants
  // represent different subscriptions, therefor we have a mapping
  // on each variant for the correct subscription. Example: `12` mapping
  // for 12 month subscription in recharge
  const getSubscriptionFrequency = metafields =>
    metafields?.find(
      metafield =>
        metafield.name === SUBSCRIPTION_FREQUENCY_METAFIELD ||
        metafield.key === SUBSCRIPTION_FREQUENCY_METAFIELD
    )?.value;

  // Call Shopify API for a productID to get selling plans (subscriptions)
  const getSellingPlan = async (productId, metafields) => {
    const { product } = await shopifyClient.request(
      FETCH_PRODUCT_UPDATE_QUERY,
      {
        id: productId,
      }
    );

    const productDeliveryFrequency = getSubscriptionFrequency(metafields);

    // Turn complex graphql structure into {id: string, name: string}[]
    // This supports both pre/post recharge upgrade
    const simplifiedSellingPlans = product?.sellingPlanGroups?.nodes?.reduce(
      (prev, item) => {
        if (item.sellingPlans?.nodes) {
          prev.push(...item.sellingPlans.nodes);
        }
        return prev;
      },
      []
    );
    const sellingPlan = simplifiedSellingPlans.find(
      node =>
        node.name.toLowerCase() ===
          `delivery every ${productDeliveryFrequency} months` ||
        node.name.toLowerCase() ===
          `delivery every ${productDeliveryFrequency} days`
    );

    return sellingPlan;
  };

  const getCheckoutUrl = async plan => {
    setLoading(true);

    const Buffer = await import('buffer')
      .then(module => {
        const buffer = module.Buffer;
        return buffer;
      })
      .catch(error => console.log(error));

    const attributes = [];
    const buyerIdentity = {};
    const discountCodes = [];
    const metafields = [];
    const note = '';

    const sellingPlan = await getSellingPlan(
      plan?.product?.store?.gid,
      plan?.productVariant?.store?.metafields
    );

    const lineItem = {
      attributes: [],
      merchandiseId: plan?.productVariant?.store?.gid,
      quantity: 1,
    };

    const sellingPlanId = sellingPlan?.id;

    if (sellingPlanId) {
      lineItem.sellingPlanId = Buffer.from(sellingPlanId).toString('base64');
    }

    // Add Pet Name to lineItem attributes,
    // so that it shows up in the checkout screen.
    if (user?.petName) {
      lineItem.attributes.push({
        key: 'Pet Name',
        value: user.petName,
      });
    }

    const lines = [lineItem];

    // Add any IOT fields to Order Attributes,
    // so that it is not visible in checkout screen.
    if (user?.email) {
      attributes.push({
        key: 'Email',
        value: user.email,
      });
    }

    if (user?.iotUserId) {
      attributes.push({
        key: 'IOT User ID',
        value: user.iotUserId,
      });
    }

    if (user?.iotGroupId) {
      attributes.push({
        key: 'IOT Group ID',
        value: user.iotGroupId,
      });
    }

    if (user?.deviceId) {
      attributes.push({
        key: 'Device ID',
        value: user.deviceId,
      });
    }

    if (user?.collarId) {
      attributes.push({
        key: 'Collar ID',
        value: user.collarId,
      });
    }

    if (user?.thingName) {
      attributes.push({
        key: 'Thing Name',
        value: user.thingName,
      });
    }

    if (user?.petName) {
      attributes.push({
        key: 'Pet Name',
        value: user.petName,
      });
    }

    if (user?.petId) {
      attributes.push({
        key: 'Pet ID',
        value: user.petId,
      });
    }

    if (dealerContext?.storedDealer) {
      attributes.push({
        key: 'Dealer ID',
        value: dealerContext.storedDealer?.id?.toString() || '',
      });
    }

    buyerIdentity.customerAccessToken = user.customerAccessToken;
    buyerIdentity.email = user.email;

    try {
      const checkoutUrl = await createCheckoutUrl(
        attributes,
        buyerIdentity,
        discountCodes,
        lines,
        metafields,
        note
      );

      if (checkoutUrl) {
        window.location = checkoutUrl;
      } else {
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initSubscriptionsComponent = async () => {
      const urlParams = new URLSearchParams(window.location.search);

      try {
        const encodedToken = urlParams.get('token');
        let userInput;

        if (encodedToken) {
          const decodedString = decodeURIComponent(atob(encodedToken));

          // Remove non-printable characters
          const cleanString = decodedString.replace(/[^\x20-\x7E]/g, '');

          // Parse the JSON
          userInput = JSON.parse(cleanString);

          if (userInput) {
            const iotUser = {
              email: userInput?.email,
              iotUserId: userInput?.iotUserId,
              iotGroupId: userInput?.iotGroupId,
              deviceId: userInput?.deviceId,
              collarId: userInput?.collarId,
              thingName: userInput?.thingName,
              petName: userInput?.petName,
              petId: userInput?.petId,
              customerAccessToken: userInput?.customerAccessToken,
              customerAccessTokenExpiresAt:
                userInput?.customerAccessTokenExpiresAt,
            };
            await validationSchema.validate(iotUser);
            setUser(iotUser);
          }
        }

        if (!encodedToken && !userInput) {
          setErrorMessage(genericErrorMessage);
        } else {
          setErrorMessage('');
        }
      } catch (err) {
        if (err.name === 'ValidationError') {
          console.error(err.errors);
          setErrorMessage(err.errors[0]);
        } else {
          console.error(err);
          setErrorMessage(genericErrorMessage);
        }
      }

      setLoading(false);
    };
    initSubscriptionsComponent();
  }, []);

  return (
    <>
      {loading && (
        <SpinnerContainer>
          <Spinner color={theme.colors.brandPrimary} />
        </SpinnerContainer>
      )}

      {!loading && countryCode !== 'US' && (
        <div>
          <MessageBox
            type="error"
            messageList={['This product is unavailable for selected region.']}
          />
        </div>
      )}

      {!loading && countryCode === 'US' && (
        <div>
          {errorMessage && (
            <MessageBox type="error" messageList={[errorMessage]} />
          )}
          {value?.heading && (
            <SubscriptionsHeader>
              <h2>{value.heading}</h2>
              {value?.subHeading && <p>{value.subHeading}</p>}
            </SubscriptionsHeader>
          )}

          <SubscriptionsContainer>
            {value?.plans.map(plan => (
              <PlanContainer key={plan?.description || plan?.price}>
                {plan?.description && (
                  <p>{plan.description?.replaceAll('\\n', '\n')}</p>
                )}

                {plan?.showPlanPrice && (
                  <>
                    {plan?.oldPrice && <OldPrice>{plan.oldPrice}</OldPrice>}
                    {plan?.price && <Price>{plan.price}</Price>}
                  </>
                )}

                <ButtonContainer>
                  <Button
                    onClick={async () => {
                      await getCheckoutUrl(plan);
                    }}
                  >
                    {plan?.ctaLabel || 'Select Plan'}
                  </Button>
                </ButtonContainer>

                {plan?.hasFreeTrial && (
                  <FreeTrial>
                    {plan?.freeTrial?.replaceAll('\\n', '\n')}
                  </FreeTrial>
                )}
              </PlanContainer>
            ))}
            {value?.terms && (
              <TermsContainer>
                <BlockBuilder
                  blocks={value.terms}
                  getInternalLink={getInternalLink}
                />
              </TermsContainer>
            )}
          </SubscriptionsContainer>
        </div>
      )}
    </>
  );
}

BlockBuilderSubscriptionGrid.propTypes = {
  value: PropTypes.shape({
    heading: PropTypes.string,
    subHeading: PropTypes.string,
    plans: PropTypes.arrayOf(
      PropTypes.shape({
        product: PropTypes.shape({}),
        productVariant: PropTypes.shape({}),
        description: PropTypes.string,
        showPlanPrice: PropTypes.bool,
        price: PropTypes.string,
        oldPrice: PropTypes.string,
        hasFreeTrial: PropTypes.bool,
        freeTrial: PropTypes.string,
        ctaLabel: PropTypes.string,
      })
    ),
    terms: PropTypes.shape({}),
  }).isRequired,
  getInternalLink: PropTypes.func.isRequired,
};

export default BlockBuilderSubscriptionGrid;
